import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, ImageList, ImageListItem } from '@mui/material';
import { PhotoLibrary as PhotoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import { SitePhotoService } from '../../../services/SitePhotoService';
import { SitePhoto, isVideoPhoto } from '../../../models/SitePhoto';

interface PhotosWidgetProps {
  projectId: number;
}

const PhotosWidget: React.FC<PhotosWidgetProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<SitePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    SitePhotoService.list(projectId)
      .then((data) => {
        if (!cancelled) setPhotos(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const preview = photos.slice(0, 6);

  return (
    <WidgetCard
      title={`동영상 및 이미지 보관함 ${photos.length > 0 ? `(${photos.length}개)` : ''}`}
      icon={<PhotoIcon fontSize="small" />}
      actionLabel="갤러리 열기"
      onAction={() => navigate(`/project/${projectId}/photos`)}
      testId="widget-photos"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : preview.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          현장 사진(랙·외관·케이블 등)을 폰으로 찍어 업로드하세요.
        </Typography>
      ) : (
        <ImageList cols={3} gap={4} sx={{ m: 0 }} rowHeight={64}>
          {preview.map((p) => (
            <ImageListItem key={p.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
              {isVideoPhoto(p) ? (
                // 동영상은 <img> 로 그리면 엑박 — 첫 프레임을 썸네일로 보여준다.
                <video
                  src={SitePhotoService.fileUrl(p.id)}
                  muted
                  preload="metadata"
                  style={{ width: '100%', height: 64, objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <img
                  src={SitePhotoService.fileUrl(p.id)}
                  alt={p.original_name}
                  loading="lazy"
                  style={{ width: '100%', height: 64, objectFit: 'cover' }}
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}
    </WidgetCard>
  );
};

export default PhotosWidget;
